import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "../../sass/files/freeQuote.scss";
import logo from "../../assets/images/logo/logowhite.png";
import useCreateFreeQuoteMutation from "../../hooks/mutations/freeQuote/createFreeQuoteMutation";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const FreeQuote = () => {
  const navigate = useNavigate();
  const freeQuoteMutation = useCreateFreeQuoteMutation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    projectType: "",
    title: "",
    description: "",
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const MAX_SIZE_MB = 16;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const allowedFileTypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
    "image/gif",
    "application/pdf",
    "application/zip",
    "application/x-zip-compressed",
    "application/vnd.rar",
    "application/x-rar-compressed",
    "model/stl",
    "model/obj",
  ];

  const allowedExtensions = [".stl", ".obj", ".zip", ".rar", ".pdf"];

  const isAllowedFile = (file) => {
    const fileName = file.name.toLowerCase();

    return (
      file.type.startsWith("image/") ||
      allowedFileTypes.includes(file.type) ||
      allowedExtensions.some((ext) => fileName.endsWith(ext))
    );
  };

  const getFilePreviewUrl = (file) => {
    if (file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }

    return null;
  };

  const getFileExtension = (fileName) => {
    return fileName.split(".").pop()?.toUpperCase() || "FILE";
  };

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleFilesChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) return;

    const validFiles = selectedFiles.filter(isAllowedFile);
    const invalidFiles = selectedFiles.filter((file) => !isAllowedFile(file));

    if (invalidFiles.length > 0) {
      alert("Only Image, STL, OBJ, ZIP, RAR or PDF files are accepted.");
    }

    setFiles((prevFiles) => [...prevFiles, ...validFiles]);

    e.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.description) {
      alert("Please fill name, email and project description.");
      return;
    }

    if (!files.length) {
      alert("Please upload at least one reference image or file.");
      return;
    }

    const totalSize = files.reduce((acc, file) => acc + file.size, 0);

    if (totalSize > MAX_SIZE_BYTES) {
      alert(`Total file size should be under ${MAX_SIZE_MB} MB.`);
      return;
    }

    setLoading(true);
    setMessage("");

    freeQuoteMutation.mutate(
      {
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp ? `+${form.whatsapp}` : "",
        projectType: form.projectType,
        title: form.title || "Free Quote Request",
        notes: form.description,
        images: files,
      },
      {
        onSuccess: () => {
          setMessage("Your request has been sent. We will reply by email soon.");

          setForm({
            name: "",
            email: "",
            whatsapp: "",
            projectType: "",
            title: "",
            description: "",
          });

          setFiles([]);
          setLoading(false);
        },
        onError: (error) => {
          console.log(error);
          alert("Something went wrong. Please try again.");
          setLoading(false);
        },
      }
    );
  };

  return (
    <section className="free-quote-page">
      <header className="free-quote-navbar">
        <Link to="/" className="free-quote-logo">
          <img src={logo} alt="3D Spot" />
        </Link>

        <div className="free-quote-nav-actions">
          <button onClick={() => navigate("/login")} className="quote-login-btn">
            Login
          </button>

          <button
            onClick={() => navigate("/dashboard/create")}
            className="quote-create-btn"
          >
            Create Order
          </button>
        </div>
      </header>

      <main className="free-quote-wrapper">
        <div className="free-quote-left">
          <p className="quote-small-title">Free Quote</p>

          <h1>
            Send Your Photo.
            <br />
            Get A Custom 3D Model Quote.
          </h1>

          <p className="quote-description">
            Upload your image, STL, sketch, or idea. Our team will review it and
            reply with pricing and timeline by email.
          </p>

          <div className="quote-points">
            <span>No account needed</span>
            <span>Manual artist-made 3D models</span>
            <span>Print-ready STL files</span>
            <span>Fast reply by email</span>
          </div>
        </div>

        <div className="free-quote-form-box">
          <h2>Request a Free Quote</h2>
          <p>
            Share your reference and project details. We’ll check it and reply
            soon.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="quote-form-row">
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleChange}
              />

              <input
                type="email"
                name="email"
                placeholder="Your email"
                value={form.email}
                onChange={handleChange}
              />
            </div>

            <div className="quote-form-row">
              <div className="quote-phone-input-wrapper">
                <PhoneInput
                  country={"us"}
                  value={form.whatsapp}
                  onChange={(phone) =>
                    setForm((prev) => ({
                      ...prev,
                      whatsapp: phone,
                    }))
                  }
                  inputProps={{
                    name: "whatsapp",
                    placeholder: "WhatsApp / phone optional",
                  }}
                  enableSearch={true}
                  disableSearchIcon={false}
                  countryCodeEditable={false}
                />
              </div>

              <select
                name="projectType"
                value={form.projectType}
                onChange={handleChange}
              >
                <option value="">Project type</option>
                <option value="Photo to STL">Photo to STL</option>
                <option value="Custom Bust">Custom Bust</option>
                <option value="Full Body Character">Full Body Character</option>
                <option value="Pet Sculpture">Pet Sculpture</option>
                <option value="STL Repair">STL Repair</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <input
              type="text"
              name="title"
              placeholder="Project title e.g. Dog bust, superhero figure"
              value={form.title}
              onChange={handleChange}
            />

            <textarea
              name="description"
              placeholder="Describe what you want us to make..."
              value={form.description}
              onChange={handleChange}
              rows="6"
            />

            <label className="quote-file-upload">
              <input
                type="file"
                multiple
                accept="image/*,.stl,.obj,.zip,.rar,.pdf"
                onChange={handleFilesChange}
              />

              <div>
                <strong>
                  {files.length
                    ? `${files.length} file(s) selected`
                    : "Upload reference image / STL file"}
                </strong>

                <span>Image, STL, OBJ, ZIP, RAR or PDF accepted</span>
              </div>
            </label>

            {files.length > 0 && (
              <div className="quote-file-preview-grid">
                {files.map((file, index) => {
                  const previewUrl = getFilePreviewUrl(file);
                  const isImage = file.type.startsWith("image/");

                  return (
                    <div
                      className="quote-file-preview-card"
                      key={`${file.name}-${file.size}-${index}`}
                    >
                      <button
                        type="button"
                        className="quote-file-remove-btn"
                        onClick={() => removeFile(index)}
                        aria-label="Remove file"
                      >
                        ×
                      </button>

                      {isImage && previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={file.name}
                          className="quote-file-preview-image"
                        />
                      ) : (
                        <div className="quote-file-preview-icon">
                          {getFileExtension(file.name)}
                        </div>
                      )}

                      <p title={file.name}>{file.name}</p>
                    </div>
                  );
                })}
              </div>
            )}

            <button type="submit" disabled={loading} className="quote-submit-btn">
              {loading ? "Sending..." : "Send Free Quote Request"}
            </button>

            {message && <p className="quote-success-message">{message}</p>}
          </form>
        </div>
      </main>
    </section>
  );
};

export default FreeQuote;
